import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Balão de dica reusável. Desktop: hover 0.5 s. Celular: long-press 0.4 s
 * (sem acionar o botão embaixo). Fecha ao tocar fora ou em 4 s no celular.
 * Wrapper é `<div className="relative">` — passe `className` extra para
 * ajustar o layout (ex.: `w-full` dentro de grids). Nunca cobre o filho.
 */
export default function Dica({ id, texto, className = "", style, children }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const showTimer = useRef(null);
  const closeTimer = useRef(null);
  const suppressClick = useRef(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !texto) return undefined;
    const onTouchStart = () => {
      clearTimeout(showTimer.current);
      showTimer.current = setTimeout(() => {
        setOpen(true);
        suppressClick.current = true;
      }, 400);
    };
    const onTouchEnd = () => {
      clearTimeout(showTimer.current);
      if (suppressClick.current) {
        clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 4000);
      }
    };
    const onTouchMove = () => { clearTimeout(showTimer.current); };
    const onClickCapture = (e) => {
      if (suppressClick.current) {
        e.preventDefault();
        e.stopPropagation();
        suppressClick.current = false;
      }
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("click", onClickCapture, true);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("click", onClickCapture, true);
      clearTimeout(showTimer.current);
      clearTimeout(closeTimer.current);
    };
  }, [texto]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  if (!texto) return children;

  const onMouseEnter = () => {
    clearTimeout(showTimer.current);
    showTimer.current = setTimeout(() => setOpen(true), 500);
  };
  const onMouseLeave = () => {
    clearTimeout(showTimer.current);
    setOpen(false);
  };
  const onFocus = () => setOpen(true);
  const onBlur = () => setOpen(false);

  return (
    <div
      ref={wrapRef}
      className={cn("relative", className)}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocusCapture={onFocus}
      onBlurCapture={onBlur}
    >
      {children}
      {open && (
        <span
          role="tooltip"
          data-testid={id ? `dica-${id}` : "dica-tooltip"}
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 z-50"
          style={{ bottom: "calc(100% + 8px)", maxWidth: 240, minWidth: 140 }}
        >
          <span
            className="block rounded-md px-2.5 py-1.5 shadow-lg text-white text-center relative"
            style={{ background: "#003E62", fontSize: 13, lineHeight: 1.35 }}
          >
            {texto}
            <span
              className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
              style={{ background: "#003E62", bottom: -4 }}
            />
          </span>
        </span>
      )}
    </div>
  );
}
